import { AgentDto, ToolDto } from '@shared/services/apiService/apiService';

export interface McpServerConfig {
  url: string;
  headers: Record<string, string>;
  transportType: string;
}

export interface HeaderItem {
  key: string;
  value: string;
}

export interface McpToolFormData {
  id?: string;
  mcpName: string;
  agentId?: string | null;
  mcpServerConfig: {
    url: string;
    headers: HeaderItem[];
    transportType: string;
  };
}

export interface McpToolJsonData {
  mcpName: string;
  agentId?: string | null;
  mcpServerConfig: {
    url: string;
    headers?: Record<string, string>;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface McpToolRawJson {
  id?: string;
  mcpName: string;
  agentId?: string | null;
  mcpServerConfig: Record<string, unknown>;
}

export interface McpToolsSectionProps {
  campaignId: string;
  agents: AgentDto[];
  tools: ToolDto[];
  refetch: () => void;
  isCampaignActive: boolean;
}

export interface McpToolCardProps {
  tool: ToolDto;
  agents: AgentDto[];
  onEdit: (tool: ToolDto) => void;
  onDelete: (toolId: string) => void;
  disabled: boolean;
}

export interface ExistingMcpTool {
  mcpName: string;
  agentId?: string | null;
  id?: string;
}

export interface McpToolFormProps {
  initialData?: McpToolFormData | null;
  agents: AgentDto[];
  existingTools: ExistingMcpTool[];
  onSubmit: (data: McpToolFormData) => Promise<void>;
  onChange?: (data: McpToolFormData) => void;
  onCancel: () => void;
  isLoading: boolean;
  disabled: boolean;
  fixedAgentId?: string | null;
}

export interface McpToolPopupProps {
  open: boolean;
  onClose: () => void;
  initialData?: McpToolFormData | null;
  initialRawJson?: McpToolJsonData | null;
  agents: AgentDto[];
  existingTools: ExistingMcpTool[];
  onSubmit: (data: McpToolFormData) => Promise<void>;
  onSubmitRawJson: (data: McpToolRawJson) => Promise<void>;
  isLoading: boolean;
  disabled: boolean;
  fixedAgentId?: string | null;
}

export const MCP_JSON_TEMPLATE: McpToolJsonData = {
  mcpName: '',
  agentId: null,
  mcpServerConfig: {
    url: '',
    headers: {},
    transportType: 'streamable-http',
  },
};

export const getEmptyMcpToolFormData = (): McpToolFormData => ({
  mcpName: '',
  agentId: null,
  mcpServerConfig: {
    url: '',
    headers: [],
    transportType: 'streamable-http',
  },
});

export const toolDtoToFormData = (tool: ToolDto): McpToolFormData => {
  const config = tool.mcpServerConfig as McpServerConfig | undefined;

  const headersArray: HeaderItem[] = config?.headers
    ? Object.entries(config.headers).map(([key, value]) => ({ key, value }))
    : [];

  // Extract agentId from top level or from mcpServerConfig
  const agentId = tool.agentId || (config as any)?.agentId || null;

  return {
    id: tool.id,
    mcpName: tool.mcpName || '',
    agentId: agentId,
    mcpServerConfig: {
      url: config?.url || '',
      headers: headersArray,
      transportType: config?.transportType || 'streamable-http',
    },
  };
};

export const toolDtoToRawJson = (tool: ToolDto): McpToolJsonData => {
  const config = tool.mcpServerConfig as Record<string, unknown> | undefined;

  // Extract agentId from top level or from mcpServerConfig
  const agentId = tool.agentId || (config?.agentId as string) || null;

  return {
    mcpName: tool.mcpName || '',
    agentId: agentId,
    mcpServerConfig: {
      url: (config?.url as string) || '',
      ...(config || {}),
    },
  };
};

export const headersArrayToObject = (headers: HeaderItem[]): Record<string, string> => {
  return headers
    .filter((h) => h.key.trim() !== '')
    .reduce(
      (acc, { key, value }) => {
        acc[key.trim()] = value;
        return acc;
      },
      {} as Record<string, string>,
    );
};

export const formDataToJson = (data: McpToolFormData): McpToolJsonData => ({
  mcpName: data.mcpName,
  agentId: data.agentId,
  mcpServerConfig: {
    url: data.mcpServerConfig.url,
    headers: headersArrayToObject(data.mcpServerConfig.headers),
    transportType: data.mcpServerConfig.transportType,
  },
});

export const jsonToFormData = (json: McpToolJsonData, id?: string): McpToolFormData => ({
  id,
  mcpName: json.mcpName || '',
  agentId: json.agentId || null,
  mcpServerConfig: {
    url: json.mcpServerConfig?.url || '',
    headers: json.mcpServerConfig?.headers
      ? Object.entries(json.mcpServerConfig.headers).map(([key, value]) => ({
          key,
          value: String(value),
        }))
      : [],
    transportType: (json.mcpServerConfig?.transportType as string) || 'streamable-http',
  },
});

export interface JsonValidationResult {
  valid: boolean;
  error?: string;
  data?: McpToolJsonData;
}

export const validateMcpJson = (jsonString: string): JsonValidationResult => {
  try {
    const parsed = JSON.parse(jsonString);

    if (!parsed.mcpName || typeof parsed.mcpName !== 'string' || !parsed.mcpName.trim()) {
      return { valid: false, error: 'mcpName is required' };
    }

    if (!parsed.mcpServerConfig || typeof parsed.mcpServerConfig !== 'object') {
      return { valid: false, error: 'mcpServerConfig is required' };
    }

    if (
      !parsed.mcpServerConfig.url ||
      typeof parsed.mcpServerConfig.url !== 'string' ||
      !parsed.mcpServerConfig.url.trim()
    ) {
      return { valid: false, error: 'mcpServerConfig.url is required' };
    }

    return { valid: true, data: parsed as McpToolJsonData };
  } catch {
    return { valid: false, error: 'Invalid JSON format' };
  }
};
